import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { OpenModal, closeModal, setMainLoading } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import Modal from "../../Services/Modal";
import moment from "moment";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";
import { Scanner } from "@yudiel/react-qr-scanner";
import Select from "react-select";
import PieChart from "../../Services/Charts/PieChart";
import LineChart from "../../Services/Charts/LineChart";
import Pagination from "../../Services/Pagination";
import { ATTENDANCE_STATUS_LABELS, EVENT_MODE_LABEL_MAP } from "./constants";
import { hasEventAttendanceStarted } from "./divisionRegistrationEventUtils";
import { useNavigate } from "react-router-dom";
import useConfirm from "../../Services/Confirmation";

const MEMBERS_PER_PAGE = 25;
const MEMBER_STATUS_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "no_response", label: "Not Responded" },
    { value: "present", label: "Present / Late" },
    { value: "absent", label: "Absent" },
    { value: "other", label: "Other" },
];

const DivisionRegistrationEventView = ({ eventId, onRefresh, scrollToRegister = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const customConfirm = useConfirm();
    const { userData, accessToken } = useSelector((state) => state.user);
    const [eventData, setEventData] = useState({});
    const [membersList, setMembersList] = useState([]);
    const [membersTotal, setMembersTotal] = useState(0);
    const [membersPage, setMembersPage] = useState(1);
    const [membersSearch, setMembersSearch] = useState("");
    const [membersStatus, setMembersStatus] = useState("");
    const [registeredUserIds, setRegisteredUserIds] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const searchDebounceRef = useRef(null);
    const scanProcessingRef = useRef(false);
    const [dailySummary, setDailySummary] = useState([]);
    const [divisionStats, setDivisionStats] = useState([]);
    const [divisionAttendancePercentage, setDivisionAttendancePercentage] = useState([]);
    const [topAbsentMembers, setTopAbsentMembers] = useState([]);
    const [dateRangeDays, setDateRangeDays] = useState([]);
    const [attendanceDate, setAttendanceDate] = useState(moment().format("YYYY-MM-DD"));
    const [filterDivId, setFilterDivId] = useState(0);
    const [divList, setDivList] = useState([]);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [availableMembers, setAvailableMembers] = useState([]);
    const [loadingAvailableMembers, setLoadingAvailableMembers] = useState(false);
    const [registerDivId, setRegisterDivId] = useState(
        ["captain", "div_admin"].includes(userData.role_slug) ? parseInt(userData.user_div, 10) : 0
    );
    const [qrPopup, setQrPopup] = useState(false);
    const [qrScannerPopup, setQrScannerPopup] = useState(false);
    const [alreadyMarked, setAlreadyMarked] = useState(false);
    const [markedTime, setMarkedTime] = useState("");
    const [isMemberView, setIsMemberView] = useState(false);
    const [memberAttendanceByDay, setMemberAttendanceByDay] = useState([]);

    const isSuperOrOrg = ["super_admin", "org_admin"].includes(userData.role_slug);
    const isDivLeader = ["captain", "div_admin"].includes(userData.role_slug);
    const isCaptain = userData.role_slug === "captain";
    const isMemberViewer = !isSuperOrOrg && !isDivLeader;

    const registrationOpen = useMemo(() => {
        if (eventData.registration_start_date && eventData.registration_end_date) {
            const today = moment().startOf("day");
            const start = moment(eventData.registration_start_date).startOf("day");
            const end = moment(eventData.registration_end_date).startOf("day");
            return today.isSameOrAfter(start) && today.isSameOrBefore(end);
        }
        return !!eventData.registration_open;
    }, [eventData.registration_start_date, eventData.registration_end_date, eventData.registration_open]);

    const eventAttendanceStarted = useMemo(
        () => hasEventAttendanceStarted(eventData.event_start_date, eventData.event_start_time),
        [eventData.event_start_date, eventData.event_start_time]
    );

    const canRegister = isSuperOrOrg || (isDivLeader && registrationOpen && !eventAttendanceStarted);
    const canRemoveMember = eventAttendanceStarted
        ? isSuperOrOrg
        : (isSuperOrOrg || (isDivLeader && registrationOpen));
    const canManageAll = isSuperOrOrg;
    const canRevertAttendance = isSuperOrOrg || isDivLeader;

    const loadEvent = ({
        date = attendanceDate,
        divId = filterDivId,
        page = membersPage,
        search = membersSearch,
        status = membersStatus,
        lightweight = false,
    } = {}) => {
        setMembersList([]);
        if (lightweight) {
            setLoadingMembers(true);
        } else {
            dispatch(setMainLoading(true));
        }
        const payload = {
            event_id: eventId,
            attendance_date: date,
            members_page: page,
            members_per_page: MEMBERS_PER_PAGE,
            members_search: search,
            members_status: status,
        };
        if (divId > 0) payload.div_id = divId;
        if (lightweight || isMemberViewer) payload.skip_qr = 1;

        PostAxios(`event/view-event-by-id`, payload, accessToken)
            .then(({ data }) => {
                if (String(data.status) === "1") {
                    if (!lightweight) {
                        setEventData((prev) => ({
                            ...data.data,
                            event_qr: data.data?.event_qr || prev.event_qr,
                        }));
                        setDailySummary(data.dailySummary || []);
                        setDivisionStats(data.divisionStats || []);
                        setDivisionAttendancePercentage(data.divisionAttendancePercentage || []);
                        setTopAbsentMembers(data.topAbsentMembers || []);
                        setDateRangeDays(data.dateRangeDays || []);
                        if (data.attendanceDate) setAttendanceDate(data.attendanceDate);
                    }
                    if (data.isMemberView) setIsMemberView(true);
                    else if (!lightweight) setIsMemberView(false);
                    if (data.memberAttendanceByDay) setMemberAttendanceByDay(data.memberAttendanceByDay);
                    setMembersList(data.membersList || []);
                    setMembersTotal(data.membersTotal || 0);
                    setMembersPage(data.membersPage || page);
                    if (data.registeredUserIds) setRegisteredUserIds(data.registeredUserIds);
                    if (data.selfAttendance) {
                        setAlreadyMarked(!!data.selfAttendance.marked);
                        setMarkedTime(data.selfAttendance.response_on || "");
                    } else {
                        const selfMarked = (data.membersList || []).find(
                            (item) => parseInt(item.user_id, 10) === parseInt(userData.id, 10) && parseInt(item.response_status, 10) > 0
                        );
                        setAlreadyMarked(!!selfMarked);
                        setMarkedTime(selfMarked?.response_on || "");
                    }
                } else {
                    dispatch(setToastData({
                        message: (data.errors && Object.values(data.errors).length > 0)
                            ? ConvertErrorArrayToString(data.errors)
                            : (data.message || "Unable to load event details."),
                        isHtml: true,
                        type: "error",
                        showCloseBtn: true
                    }));
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => {
                if (lightweight) setLoadingMembers(false);
                else dispatch(setMainLoading(false));
            });
    };

    const reloadMembers = (overrides = {}) => {
        loadEvent({ ...overrides, lightweight: true });
    };

    useEffect(() => {
        loadEvent();
        if (isSuperOrOrg) {
            PostAxios(`masters/division-list`, {
                org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id,
                page: 1,
                perPage: 1000,
            }, accessToken).then(({ data }) => {
                if (data.status === "1") setDivList(data.data);
            });
        }
    }, [eventId]);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, []);

    const handleMembersSearchChange = (value) => {
        setMembersSearch(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setMembersPage(1);
            reloadMembers({ page: 1, search: value });
        }, 400);
    };

    useEffect(() => {
        if (scrollToRegister && canRegister) {
            setTimeout(() => {
                document.getElementById("register-division-members")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 400);
        }
    }, [scrollToRegister, canRegister, eventData.event_title]);

    const attendanceWindowOpen = useMemo(() => {
        if (!eventData.event_start_date) return false;
        const today = moment().format("YYYY-MM-DD");
        if (!moment(today).isBetween(eventData.event_start_date, eventData.event_end_date, "day", "[]")) return false;
        return moment().isBetween(
            moment(`${today} ${eventData.event_start_time}`),
            moment(`${today} ${eventData.attendence_time}`),
            "minute",
            "[]"
        );
    }, [eventData]);

    const canMarkAttendance = useMemo(() => {
        if (!eventData.event_start_date || !attendanceDate) return false;
        if (moment().isBefore(eventData.event_start_date, "day")) return false;
        if (!moment(attendanceDate).isBetween(eventData.event_start_date, eventData.event_end_date, "day", "[]")) return false;
        if (moment(attendanceDate).isAfter(moment(), "day")) return false;
        return true;
    }, [eventData.event_start_date, eventData.event_end_date, attendanceDate]);

    const canMarkForMember = (member) => {
        if (!canMarkAttendance) return false;
        if (canManageAll) return true;
        if (isDivLeader && parseInt(member.user_div, 10) === parseInt(userData.user_div, 10)) {
            return attendanceWindowOpen && moment(attendanceDate).isSame(moment(), "day");
        }
        return false;
    };

    const eventStillActive = useMemo(() => {
        if (!eventData.event_end_date || !eventData.event_end_time) return false;
        return moment(`${eventData.event_end_date} ${eventData.event_end_time}`).isAfter(moment());
    }, [eventData.event_end_date, eventData.event_end_time]);

    const canRevertForMember = (member) => {
        if (!canRevertAttendance) return false;
        if (!eventStillActive || parseInt(member.response_status, 10) <= 0) return false;
        return canMarkForMember(member);
    };

    const canSelfRevert = eventStillActive && attendanceWindowOpen && alreadyMarked;

    const getQrFileName = () => {
        const slug = (eventData.event_title || "event-qr")
            .replace(/[^\w\s-]/g, "")
            .trim()
            .replace(/\s+/g, "-")
            .toLowerCase();
        return `${slug || "event-qr"}-${eventId}.png`;
    };

    const getQrBlob = () => {
        if (!eventData.event_qr) return null;
        const byteCharacters = atob(eventData.event_qr);
        const byteNumbers = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([byteNumbers], { type: "image/png" });
    };

    const downloadQrCode = async () => {
        const blob = getQrBlob();
        if (!blob) {
            dispatch(setToastData({ message: "QR code is not available.", type: "error", showCloseBtn: true }));
            return;
        }
        const fileName = getQrFileName();
        const file = new File([blob], fileName, { type: "image/png" });

        if (navigator.share && navigator.canShare?.({ files: [file] })) {
            try {
                await navigator.share({
                    files: [file],
                    title: eventData.event_title,
                    text: "Event attendance QR code",
                });
                return;
            } catch (err) {
                if (err?.name === "AbortError") return;
            }
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
            window.open(url, "_blank");
            dispatch(setToastData({
                message: "If download did not start, long-press the opened image and choose Save to Photos.",
                type: "success",
                showCloseBtn: true,
            }));
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            return;
        }

        URL.revokeObjectURL(url);
        dispatch(setToastData({ message: "QR code downloaded.", type: "success", showCloseBtn: true }));
    };

    const handlePrintQr = () => {
        const blob = getQrBlob();
        if (!blob) {
            dispatch(setToastData({ message: "QR code is not available.", type: "error", showCloseBtn: true }));
            return;
        }
        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            dispatch(setToastData({ message: "Please allow popups to print the QR code.", type: "error", showCloseBtn: true }));
            return;
        }
        const blobURL = URL.createObjectURL(blob);
        printWindow.document.write(`<html>
        <head>
          <title>${eventData.event_title} QR</title>
          <style>
            @media print {
              body, html { margin: 0; padding: 0; height: 100%; width: 100%; }
              body {
                display: flex; justify-content: center; align-items: center;
                flex-direction: column; height: 100vh; width: 100%;
              }
              div, h1 { width: 100%; display: block; text-align: center; }
              img { width: 80%; height: auto; display: block; margin: 0 auto; }
            }
          </style>
        </head>
        <body>
          <div><img src="${blobURL}" alt="Event QR" /></div>
          <h1>${eventData.event_title}</h1>
        </body>
        </html>`);
        printWindow.document.close();
        printWindow.onload = () => {
            setTimeout(() => {
                try {
                    printWindow.print();
                } catch (error) {
                    dispatch(setToastData({ message: "Failed to print. Please try again.", type: "error", showCloseBtn: true }));
                }
                printWindow.close();
                URL.revokeObjectURL(blobURL);
            }, 500);
        };
    };

    const markAttendance = (status, userId, userDiv, qrId = eventData.encryptedId) => {
        if (!navigator.onLine) {
            dispatch(setToastData({ message: "Internet connection is required to mark attendance.", type: "error", showCloseBtn: true }));
            return;
        }
        const isSelfMark = parseInt(userId, 10) === parseInt(userData.id, 10);
        if (isSelfMark && alreadyMarked) {
            dispatch(setToastData({ message: "Attendance has already been marked for today.", type: "error", showCloseBtn: true }));
            return;
        }
        dispatch(setMainLoading(true));
        PostAxios(`event/mark-attendance`, {
            event_id: qrId,
            mem_id: userId,
            attendance_response: status,
            div_id: userDiv,
            attendance_date: isSelfMark ? moment().format("YYYY-MM-DD") : attendanceDate,
            client_online: navigator.onLine ? 1 : 0,
        }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                    if (isSelfMark) {
                        setAlreadyMarked(true);
                        setMarkedTime(moment().format("YYYY-MM-DD HH:mm:ss"));
                    }
                    reloadMembers();
                    if (onRefresh) onRefresh();
                } else {
                    dispatch(setToastData({
                        message: Object.values(data.errors || {}).length ? ConvertErrorArrayToString(data.errors) : data.message,
                        type: "error",
                        showCloseBtn: true,
                    }));
                    if (isSelfMark && (data.message || "").toLowerCase().includes("already been marked")) {
                        setAlreadyMarked(true);
                    }
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => {
                scanProcessingRef.current = false;
                dispatch(setMainLoading(false));
            });
    };

    const revertAttendance = (userId, userDiv) => {
        if (!navigator.onLine) {
            dispatch(setToastData({ message: "Internet connection is required.", type: "error", showCloseBtn: true }));
            return;
        }
        const isSelfRevert = parseInt(userId, 10) === parseInt(userData.id, 10);
        dispatch(setMainLoading(true));
        PostAxios(`event/revert-attendance`, {
            event_id: eventData.encryptedId,
            mem_id: userId,
            div_id: userDiv,
            attendance_date: isSelfRevert ? moment().format("YYYY-MM-DD") : attendanceDate,
        }, accessToken)
            .then(({ data }) => {
                if (String(data.status) === "1") {
                    dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                    if (isSelfRevert) {
                        setAlreadyMarked(false);
                        setMarkedTime("");
                    }
                    reloadMembers();
                    if (onRefresh) onRefresh();
                } else {
                    dispatch(setToastData({
                        message: Object.values(data.errors || {}).length ? ConvertErrorArrayToString(data.errors) : data.message,
                        type: "error",
                        showCloseBtn: true,
                    }));
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    const registerMembers = () => {
        if (!navigator.onLine) {
            dispatch(setToastData({ message: "Internet connection is required.", type: "error", showCloseBtn: true }));
            return;
        }
        if (!selectedMembers.length) {
            dispatch(setToastData({ message: "Select members to register.", type: "error", showCloseBtn: true }));
            return;
        }
        dispatch(setMainLoading(true));
        PostAxios(`event/register-division-members`, {
            event_id: eventId,
            register_members: selectedMembers.map((m) => m.value),
            div_id: isDivLeader ? userData.user_div : (registrationDivId || userData.user_div),
        }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                    setSelectedMembers([]);
                    loadEvent();
                } else {
                    dispatch(setToastData({ message: data.message, type: "error", showCloseBtn: true }));
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    const deregisterMember = async (memId, memberName = "") => {
        if (!navigator.onLine) {
            dispatch(setToastData({ message: "Internet connection is required.", type: "error", showCloseBtn: true }));
            return;
        }
        const label = (memberName || "").trim() || "this member";
        const userChoice = await customConfirm({
            description: `Are you sure you want to remove ${label} from this event?`,
        });
        if (!userChoice) {
            return;
        }
        dispatch(setMainLoading(true));
        PostAxios(`event/deregister-division-member`, { event_id: eventId, mem_id: memId }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                    loadEvent();
                } else {
                    dispatch(setToastData({ message: data.message, type: "error", showCloseBtn: true }));
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    const exportReport = (exportType) => {
        if (!navigator.onLine) {
            dispatch(setToastData({ message: "Internet connection is required to export.", type: "error", showCloseBtn: true }));
            return;
        }
        dispatch(setMainLoading(true));
        const formData = new FormData();
        formData.append("event_id", eventData.ID || eventData.id || eventId);
        formData.append("token", accessToken);
        formData.append("export_type", exportType);
        formData.append("attendance_date", attendanceDate);
        if (filterDivId > 0) formData.append("div_id", filterDivId);
        else if (!isSuperOrOrg) formData.append("div_id", userData.user_div);

        fetch(`${process.env.REACT_APP_API_URL}event/export-division-attendance-report`, {
            method: "POST",
            body: formData,
            mode: "cors",
        })
            .then(async (response) => {
                const contentType = response.headers.get("content-type") || "";
                if (!response.ok || contentType.includes("application/json")) {
                    const text = await response.text();
                    let message = "Export failed.";
                    try {
                        const json = JSON.parse(text);
                        message = json.message || (json.errors ? ConvertErrorArrayToString(json.errors) : message);
                    } catch (parseErr) {
                        if (text) message = text;
                    }
                    throw new Error(message);
                }
                const blob = await response.blob();
                return [blob, response.headers];
            })
            .then(([blob, headers]) => {
                const disposition = headers.get("Content-Disposition");
                let fileName = `division-attendance-${exportType}-${moment().format("YYYY-MM-DD-HHmmss")}.xlsx`;
                if (disposition) {
                    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                    if (match?.[1]) fileName = match[1].replace(/['"]/g, "");
                }
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    const registrationDivId = isDivLeader ? parseInt(userData.user_div, 10) : parseInt(registerDivId, 10);

    const eventDivisionIds = useMemo(() => {
        const eventDiv = eventData.event_user_div;
        if (!eventDiv || eventDiv === "0" || eventDiv === 0) return null;
        return String(eventDiv).split(",").map((id) => parseInt(id, 10)).filter((id) => id > 0);
    }, [eventData.event_user_div]);

    const registrationDivList = useMemo(() => {
        if (!eventDivisionIds) return divList;
        return divList.filter((d) => eventDivisionIds.includes(parseInt(d.ID, 10)));
    }, [divList, eventDivisionIds]);

    const loadAvailableMembers = () => {
        if (!canRegister || !eventData.event_title) return;
        if (!registrationDivId || registrationDivId < 1) {
            setAvailableMembers([]);
            return;
        }
        setLoadingAvailableMembers(true);
        PostAxios(`users`, {
            org_id: userData.role_slug === "super_admin" ? (eventData.event_organization || 0) : userData.org_id,
            div_id: registrationDivId,
            page: 0,
            perPage: 1000,
            search: "",
        }, accessToken)
            .then(({ data }) => {
                const registeredIds = registeredUserIds.length
                    ? registeredUserIds
                    : membersList.map((m) => parseInt(m.user_id, 10));
                const options = (data.data || [])
                    .filter((m) => !registeredIds.includes(parseInt(m.id, 10)))
                    .map((m) => ({ value: m.id, label: `${m.full_name} (${m.user_its})` }));
                setAvailableMembers(options);
            })
            .catch((err) => {
                responseCatchFunc(dispatch, err);
                setAvailableMembers([]);
            })
            .finally(() => setLoadingAvailableMembers(false));
    };

    useEffect(() => {
        loadAvailableMembers();
    }, [canRegister, eventData.event_title, eventData.event_organization, registeredUserIds, registrationDivId]);

    const dailyChartOptions = useMemo(() => {
        const selectedDay = dailySummary.find((d) => d.date === attendanceDate);
        if (!selectedDay) return { series: [{ name: "Attendance", data: [] }] };
        return {
            title: { text: `Attendance - ${moment(attendanceDate).format("DD MMM YYYY")}` },
            series: [{
                name: "Count",
                colorByPoint: true,
                data: [
                    { name: "Present", y: selectedDay.present },
                    { name: "Late", y: selectedDay.late },
                    { name: "Absent", y: selectedDay.absent },
                    { name: "No Response", y: selectedDay.no_response },
                ].filter((d) => d.y > 0),
            }],
        };
    }, [dailySummary, attendanceDate]);

    const divisionChartOptions = useMemo(() => ({
        title: { text: "Division Attendance %" },
        xAxis: { categories: divisionAttendancePercentage.map((d) => d.division_name) },
        yAxis: { title: { text: "Attendance %" }, max: 100 },
        series: [{
            type: "column",
            name: "Attendance %",
            data: divisionAttendancePercentage.map((d) => d.attendance_percentage),
        }],
    }), [divisionAttendancePercentage]);

    if (!eventData.event_title) return null;

    if (isMemberView || isMemberViewer) {
        return (
            <>
                <div className="main-form-sec">
                    <div className="form-sec">
                        <div className="w-50 p-3">Location : <b>{eventData.event_location}</b></div>
                        <div className="w-50 p-3">Attendance Period : <b>{moment(eventData.event_start_date).format("DD MMM YYYY")} - {moment(eventData.event_end_date).format("DD MMM YYYY")}</b></div>
                        <div className="w-50 p-3">Daily Window : <b>{moment(eventData.event_start_time, "HH:mm:ss").format("hh:mm A")} - {moment(eventData.attendence_time, "HH:mm:ss").format("hh:mm A")}</b></div>
                        <div className="w-50 p-3">Description : <b>{eventData.event_description}</b></div>
                        <div className="px-2 my-3 w-100 d-flex flex-wrap gap-2 align-items-center">
                            {attendanceWindowOpen && !alreadyMarked && (
                                <button type="button" className="btn btn-blue" onClick={() => { setQrScannerPopup(true); dispatch(OpenModal()); }}>
                                    <i className="fas fa-camera" /> Scan QR
                                </button>
                            )}
                            {alreadyMarked && (
                                <>
                                    <span className="text-success align-self-center">Marked today at {moment(markedTime).format("DD MMM YYYY hh:mm A")}</span>
                                    {canSelfRevert && (
                                        <button type="button" className="btn btn-sm btn-blue ms-2" onClick={() => revertAttendance(userData.id, userData.user_div)}>
                                            Revert
                                        </button>
                                    )}
                                </>
                            )}
                            <button type="button" className="btn btn-blue ms-2" onClick={() => loadEvent()}><i className="fas fa-sync" /> Refresh</button>
                        </div>
                    </div>
                </div>
                <div className="main-form-sec">
                    <h4 className="mb-3 px-3">My Attendance</h4>
                    <div className="table-wrapper px-3">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {memberAttendanceByDay.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-4">No attendance records yet.</td>
                                    </tr>
                                )}
                                {memberAttendanceByDay.map((day, index) => (
                                    <tr key={`member-day-${day.date}`}>
                                        <td>{index + 1}</td>
                                        <td>{moment(day.date).format("DD MMM YYYY (dddd)")}</td>
                                        <td>{ATTENDANCE_STATUS_LABELS[parseInt(day.response_status, 10)] || "Not Responded"}</td>
                                        <td>
                                            {parseInt(day.response_status, 10) > 0 && day.response_on
                                                ? moment(day.response_on).format("hh:mm A")
                                                : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                {qrScannerPopup && (
                    <Modal classList="popup-main invoice-popup" style={{ maxWidth: 400, minHeight: 500, width: "100%" }} onClose={() => setQrScannerPopup(false)}>
                        <h3 className="text-center">{eventData.event_title}</h3>
                        {!navigator.onLine && <p className="text-danger text-center">No internet connection</p>}
                        <Scanner onScan={(result) => {
                            if (scanProcessingRef.current || alreadyMarked) return;
                            if (!navigator.onLine) {
                                dispatch(setToastData({ message: "Internet connection is required.", type: "error", showCloseBtn: true }));
                                return;
                            }
                            scanProcessingRef.current = true;
                            dispatch(closeModal());
                            setQrScannerPopup(false);
                            markAttendance(1, userData.id, userData.user_div, result[0].rawValue);
                        }} />
                    </Modal>
                )}
            </>
        );
    }

    return (
        <>
            <div className="main-form-sec">
                <div className="form-sec">
                    <div className="w-50 p-3">Event Mode : <b>{EVENT_MODE_LABEL_MAP.division_registration}</b></div>
                    <div className="w-50 p-3">Location : <b>{eventData.event_location}</b></div>
                    <div className="w-50 p-3">Registration : <b>{moment(eventData.registration_start_date).format("DD MMM YYYY")} - {moment(eventData.registration_end_date).format("DD MMM YYYY")}</b>
                        {(canManageAll || !eventAttendanceStarted) ? (
                            registrationOpen
                                ? <span className="status-btn approved ms-2" style={{float: "none"}}>Open</span>
                                : <span className="status-btn rejected bg-secondary ms-2" style={{float: "none"}}>Registration Closed</span>
                        ): <span className="status-btn rejected bg-secondary ms-2" style={{float: "none"}}>Registration Closed</span>}
                    </div>
                    <div className="w-50 p-3">Attendance Period : <b>{moment(eventData.event_start_date).format("DD MMM YYYY")} - {moment(eventData.event_end_date).format("DD MMM YYYY")}</b></div>
                    <div className="w-50 p-3">Daily Window : <b>{moment(eventData.event_start_time, "HH:mm:ss").format("hh:mm A")} - {moment(eventData.attendence_time, "HH:mm:ss").format("hh:mm A")}</b></div>
                    <div className="w-50 p-3">Description : <b>{eventData.event_description}</b></div>

                    <div className="px-2 my-3 w-100 d-flex flex-wrap gap-2 align-items-center">
                        {(canManageAll || (isCaptain && moment().isBetween(eventData.event_start_date, eventData.event_end_date, "day", "[]"))) && (
                            <button type="button" className="btn btn-blue" onClick={() => { setQrPopup(true); dispatch(OpenModal()); }}>
                                <i className="fas fa-qrcode" /> View QR
                            </button>
                        )}
                        {attendanceWindowOpen && !alreadyMarked && (
                            <button type="button" className="btn btn-blue ms-2" onClick={() => { setQrScannerPopup(true); dispatch(OpenModal()); }}>
                                <i className="fas fa-camera" /> Scan QR
                            </button>
                        )}
                        {alreadyMarked && (
                            <>
                                <span className="text-success align-self-center ms-2">Marked on {moment(markedTime).format("DD MMM YYYY hh:mm A")}</span>
                                {canSelfRevert && (
                                    <button type="button" className="btn btn-sm btn-blue ms-2" onClick={() => revertAttendance(userData.id, userData.user_div)}>
                                        Revert
                                    </button>
                                )}
                            </>
                        )}
                        {canManageAll && (
                            <button type="button" className="btn btn-blue ms-2" onClick={() => navigate("/events/edit-division-registration", { state: { event_id: eventId } })}>
                                <i className="fas fa-pencil-alt" /> Edit Event
                            </button>
                        )}
                        <button type="button" className="btn btn-blue ms-2" onClick={() => loadEvent()}><i className="fas fa-sync" /> Refresh</button>
                    </div>
                </div>

                {canRegister && (
                    <div id="register-division-members" className="form-sec my-3 px-3">
                        <h5 className="mb-3 w-100">Register Division Members</h5>
                        {!registrationOpen && !canManageAll && (
                            <p className="text-muted w-100">Registration period has ended. Only <strong>Murtaza Bhai Millwala</strong> can register or remove members now.</p>
                        )}
                        {canManageAll && (
                            <div className="form-field-group w-50">
                                <label>Division</label>
                                <select
                                    className="select-input"
                                    value={registerDivId}
                                    onChange={(e) => {
                                        setRegisterDivId(parseInt(e.target.value, 10));
                                        setSelectedMembers([]);
                                    }}
                                >
                                    <option value={0}>Select Division</option>
                                    {registrationDivList.map((d) => (
                                        <option key={d.ID} value={d.ID}>{d.division_name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {registrationDivId > 0 ? (
                            <>
                                <p className="text-muted mb-2">
                                    {loadingAvailableMembers
                                        ? "Loading division members..."
                                        : `${availableMembers.length} member(s) available to register`}
                                </p>
                                <div className="w-100 d-flex flex-wrap gap-2 align-items-center">
                                    <div className="w-50">
                                        <Select
                                            isMulti
                                            isSearchable
                                            isLoading={loadingAvailableMembers}
                                            isDisabled={loadingAvailableMembers || availableMembers.length === 0}
                                            value={selectedMembers}
                                            options={availableMembers}
                                            onChange={(selected) => {
                                                if (selected?.some((option) => option.value === "select_all")) {
                                                    setSelectedMembers(availableMembers);
                                                } else {
                                                    setSelectedMembers(selected || []);
                                                }
                                            }}
                                            placeholder={availableMembers.length ? "Select members from list..." : "All division members are already registered"}
                                            styles={{
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    maxHeight: 150,
                                                    overflow: "auto",
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div className="w-50">
                                        <button
                                            type="button"
                                            className="btn btn-blue ms-2"
                                            disabled={loadingAvailableMembers || !availableMembers.length}
                                            onClick={() => setSelectedMembers(availableMembers)}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-blue ms-2"
                                            disabled={!selectedMembers.length}
                                            onClick={() => setSelectedMembers([])}
                                        >
                                            Clear
                                        </button>
                                        <button type="button" className="btn ms-2 btn-blue" onClick={registerMembers} disabled={!selectedMembers.length}>
                                            Register Selected ({selectedMembers.length})
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="text-muted w-100">Select a division to load members.</p>
                        )}
                    </div>
                )}
            </div>
            <div className="main-form-sec d-flex flex-wrap gap-2 align-items-center">
                <div className="w-25 form-field-group mb-0">
                    <label>Attendance Date</label>
                    <select className="select-input" value={attendanceDate} onChange={(e) => {
                        const date = e.target.value;
                        setAttendanceDate(date);
                        setMembersPage(1);
                        reloadMembers({ date, page: 1 });
                    }}>
                        {dateRangeDays.map((d) => <option key={d} value={d}>{moment(d).format("DD MMM YYYY (dddd)")}</option>)}
                    </select>
                </div>
                {isSuperOrOrg && (
                    <div className="w-25 form-field-group mb-0">
                        <label>Filter Division</label>
                        <select className="select-input" value={filterDivId} onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            setFilterDivId(v);
                            setMembersPage(1);
                            reloadMembers({ divId: v, page: 1 });
                        }}>
                            <option value={0}>All Divisions</option>
                            {divList.map((d) => <option key={d.ID} value={d.ID}>{d.division_name}</option>)}
                        </select>
                    </div>
                )}
                <div className="w-50 d-flex align-items-end flex-wrap gap-2">
                    <button className="btn btn-blue " onClick={() => exportReport("single_day")}>Export Day</button>
                    <button className="btn ms-2 btn-blue" onClick={() => exportReport("combined")}>Export Combined</button>
                    <button className="btn ms-2 btn-blue" onClick={() => exportReport("cumulative_daywise")}>Export Day-wise %</button>
                    <button className="btn ms-2 btn-blue" onClick={() => exportReport("cumulative_division")}>Export Division-wise %</button>
                </div>
            </div>
            <div className="main-form-sec d-flex flex-wrap gap-2 justify-content-center align-items-center">
                <div className="w-50">
                    <PieChart options={dailyChartOptions} />
                </div>
                {canManageAll && divisionAttendancePercentage.length > 0 && (
                    <div className="w-50">
                        <LineChart options={divisionChartOptions} />
                    </div>
                )}
                {isDivLeader && (
                    <div className="w-50">
                        <h4 className="mb-3">Most Absent Members</h4>
                        {topAbsentMembers.length > 0 ? (
                            <div className="table-wrapper">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>S.No.</th>
                                            <th>Member</th>
                                            <th>ITS</th>
                                            <th>Absent Days</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topAbsentMembers.map((m, index) => (
                                            <tr key={`absent-${m.user_id}`}>
                                                <td>{index + 1}</td>
                                                <td>{(m.full_name || "").trim() || "-"}</td>
                                                <td>{m.user_its || "-"}</td>
                                                <td>{parseInt(m.absent_count, 10)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-muted mb-0">No absent members recorded during the event period.</p>
                        )}
                    </div>
                )}
            </div>
            <div className="main-form-sec">
                {isSuperOrOrg && filterDivId === 0 && membersTotal > 100 && (
                    <div className="w-100 alert alert-info mx-3 mt-3 mb-2">
                        Showing <strong>{membersTotal}</strong> registered members across all divisions.
                        Filter by division for faster loading, or use search / export for full lists.
                    </div>
                )}
                <div className="form-sec d-flex flex-wrap gap-3 align-items-end mb-3">
                    <div className="form-field-group mb-0" style={{ minWidth: 300 }}>
                        <div className="w-50">
                            <label>Search Member</label>
                        </div>
                        <div className="w-50 input-area">
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Name or ITS..."
                                value={membersSearch}
                                onChange={(e) => handleMembersSearchChange(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="form-field-group mb-0" style={{ minWidth: 220 }}>
                        <label>Status</label>
                        <select
                            className="select-input"
                            value={membersStatus}
                            onChange={(e) => {
                                const status = e.target.value;
                                setMembersStatus(status);
                                setMembersPage(1);
                                reloadMembers({ page: 1, status });
                            }}
                        >
                            {MEMBER_STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value || "all"} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="text-muted mb-2">
                        
                    </div>
                </div>
                <div className="table-main">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>S.No.</th>
                                <th>Member</th>
                                {isSuperOrOrg && <th>Division</th>}
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loadingMembers && membersList.length === 0 && (
                                <tr>
                                    <td colSpan={isSuperOrOrg ? 5 : 4} className="text-center text-muted py-4">
                                        No members found for the selected filters.
                                    </td>
                                </tr>
                            )}
                            {loadingMembers ? <tr><td colSpan={isSuperOrOrg ? 5 : 4} className="text-center text-muted py-4"><i className="fas fa-spinner fa-spin"></i> Loading members...</td></tr> : ''}
                            {membersList.map((item, index) => (
                                <tr key={`mem-${item.user_id}`}>
                                    <td>{(membersPage - 1) * MEMBERS_PER_PAGE + index + 1}</td>
                                    <td>{`${item.user_title_prefix} ${item.first_name} Bhai ${item.user_last_name}`.trim()}</td>
                                    {isSuperOrOrg && <td>{item.division_name}</td>}
                                    <td>
                                        {ATTENDANCE_STATUS_LABELS[parseInt(item.response_status)] || "Not Responded"}
                                        {parseInt(item.response_status) > 0 && <> <small>({moment(item.response_on).format("hh:mm A")})</small></>}
                                    </td>
                                    <td>
                                        {canMarkForMember(item) && parseInt(item.response_status) === 0 && (
                                            <>
                                                <button className="btn btn-sm status-btn approved me-1" onClick={() => markAttendance(1, item.user_id, item.user_div)}>Present</button>
                                                <button className="btn btn-sm status-btn rejected me-1" onClick={() => markAttendance(3, item.user_id, item.user_div)}>Absent</button>
                                            </>
                                        )}
                                        {canRevertForMember(item) && (
                                            <button className="btn btn-sm btn-blue me-1" onClick={() => revertAttendance(item.user_id, item.user_div)}>Revert</button>
                                        )}
                                        {canRemoveMember && (
                                            <button className="btn btn-sm status-btn verification-pending ms-1" style={{ float: "none" }} onClick={() => deregisterMember(item.user_id, `${item.user_title_prefix} ${item.first_name} Bhai ${item.user_last_name}`.trim())}>Remove</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    currentPage={membersPage}
                    totalCount={membersTotal}
                    pageSize={MEMBERS_PER_PAGE}
                    onPageChange={(page) => {
                        setMembersPage(page);
                        reloadMembers({ page });
                    }}
                />
                {divisionStats.length > 0 && (
                    <div className="mt-3">
                        <h4 className="mb-3">Registered by Division</h4>
                        <div className="d-flex flex-wrap gap-2 ">
                            {divisionStats.map((d) => (
                                <span key={d.div_id} className="badge bg-light text-dark border bg-light p-2 mb-2 rounded me-2">{d.division_name}: {d.registered_count}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>


            {qrPopup && (
                <Modal classList="popup-main invoice-popup" style={{ maxWidth: 500, minHeight: 500, width: "100%" }} onClose={() => setQrPopup(false)}>
                    <h3 className="text-center">Scan this QR daily during attendance window</h3>
                    <div className="my-2 text-center d-flex flex-wrap justify-content-center gap-2">
                        <button type="button" className="btn btn-sm btn-blue" onClick={downloadQrCode}>
                            <i className="fas fa-download" /> Download
                        </button>
                        <button type="button" className="btn btn-sm btn-blue ms-2" onClick={handlePrintQr}>
                            <i className="fas fa-print" /> Print
                        </button>
                    </div>
                    <img
                        src={`data:image/png;base64,${eventData.event_qr}`}
                        alt="Event QR"
                        style={{ width: "100%", height: "auto", cursor: "pointer" }}
                        onClick={downloadQrCode}
                        title="Tap to download"
                    />
                    <p className="text-center text-muted small mb-0">Tap the QR image to download on mobile</p>
                    <h3 className="text-center">{eventData.event_title}</h3>
                </Modal>
            )}

            {qrScannerPopup && (
                <Modal classList="popup-main invoice-popup" style={{ maxWidth: 400, minHeight: 500, width: "100%" }} onClose={() => setQrScannerPopup(false)}>
                    <h3 className="text-center">{eventData.event_title}</h3>
                    {!navigator.onLine && <p className="text-danger text-center">No internet connection</p>}
                    <Scanner onScan={(result) => {
                        if (scanProcessingRef.current || alreadyMarked) return;
                        if (!navigator.onLine) {
                            dispatch(setToastData({ message: "Internet connection is required.", type: "error", showCloseBtn: true }));
                            return;
                        }
                        scanProcessingRef.current = true;
                        dispatch(closeModal());
                        setQrScannerPopup(false);
                        markAttendance(1, userData.id, userData.user_div, result[0].rawValue);
                    }} />
                </Modal>
            )}
        </>

    );
};

export default DivisionRegistrationEventView;
